import { Injectable } from '@angular/core';
import { AuthTokens } from '@core/auth/auth-tokens.model';
import { PermissionModule } from '@shared/models/permission-matrix.model';
import { isAppLanguageCode } from '@shared/constants/app-languages.const';
import { StaffClinicMapper } from '@features/shared/clinics/data/mappers/staff-clinic.mapper';
import { AuthSession } from '../../domain/models/auth-session.model';
import { AuthenticatedUser } from '../../domain/models/authenticated-user.model';
import { TokenInfo } from '../../domain/models/token-info.model';
import {
  AuthenticatedUserDto,
  ClinicOwnerRegistrationTokenInfoDto,
  ResetPasswordTokenInfoDto,
  TokenResponseDto,
} from '../dtos/auth.dto';

@Injectable()
export class AuthMapper {
  // Constructed directly (never `inject()`-ed) so this mapper stays safe to build with a bare
  // `new AuthMapper()` outside an injection context -- the pattern every spec in this feature (and
  // `passkeys-layers.spec.ts`) already relies on. `StaffClinicMapper` has no dependencies of its own,
  // so doing the same here costs nothing.
  private readonly staffClinicMapper = new StaffClinicMapper();

  public toSession(dto: TokenResponseDto): AuthSession {
    return { tokens: this.toTokens(dto), user: this.toUser(dto.User) };
  }

  public toTokens(dto: TokenResponseDto): AuthTokens {
    return {
      accessToken: dto.AccessToken,
      accessTokenExpiresAtUtc: dto.AccessTokenExpiresAtUtc,
      refreshToken: dto.RefreshToken,
      refreshTokenExpiresAtUtc: dto.RefreshTokenExpiresAtUtc,
      tokenType: dto.TokenType,
    };
  }

  public toUser(dto: AuthenticatedUserDto): AuthenticatedUser {
    return {
      ...(dto.IsSessionIdentityOnly ? { isSessionIdentityOnly: true } : {}),
      userId: dto.UserId,
      publicUserId: dto.PublicUserId,
      username: dto.Username,
      accountRealm: dto.AccountRealm,
      organizationId: dto.OrganizationId,
      organizationName: dto.OrganizationName ?? null,
      homeOrganizationId: dto.HomeOrganizationId ?? dto.OrganizationId,
      organizationDefaultLanguage: isAppLanguageCode(dto.OrganizationDefaultLanguage)
        ? dto.OrganizationDefaultLanguage
        : 'es',
      provider: dto.Provider,
      providerInstance: dto.ProviderInstance,
      roleId: dto.RoleId,
      role: dto.Role,
      permissions: dto.Permissions,
      planModules: (dto.PlanModules ?? null) as readonly PermissionModule[] | null,
      firstName: dto.FirstName,
      lastName: dto.LastName,
      hasCompletedOnboarding: dto.HasCompletedOnboarding,
      avatarUrl: dto.AvatarUrl,
      staffClinics: dto.StaffOrganizations
        ? dto.StaffOrganizations.map((clinicDto) => this.staffClinicMapper.toStaffClinic(clinicDto))
        : null,
      departmentId: dto.DepartmentId,
      departmentName: dto.DepartmentName,
      homeDepartmentId: dto.HomeDepartmentId ?? null,
      homeDepartmentName: dto.HomeDepartmentName ?? null,
      clientImpersonation: dto.ClientImpersonation
        ? {
          mode: dto.ClientImpersonation.Mode,
          canWrite: dto.ClientImpersonation.CanWrite,
          externalClientId: dto.ClientImpersonation.ExternalClientId,
          departmentId: dto.ClientImpersonation.DepartmentId,
        }
        : null,
    };
  }

  public toTokenInfo(
    dto: ResetPasswordTokenInfoDto | ClinicOwnerRegistrationTokenInfoDto,
  ): TokenInfo {
    return { email: dto.Email, purpose: 'Purpose' in dto ? dto.Purpose : null };
  }
}
